import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { GlobalContext } from 'providers/GlobalProvider';
import { selectedTeamId } from '../common/selectedTeamId';
import { useGetDatabaseScreeningStatus } from 'api/bitedb/screeningHistory/screening.hooks';

import {
  runBusinessVerification,
  VerificationRunCheck,
  VerificationRunRefusal,
} from 'api/bitedb/entities/businessVerification.service';

import { buildEntityVerificationReportChecks } from './entityVerificationReportModel';

import {
  downloadVerificationReport,
  reportPreparedBy,
} from './verificationReportPdf';
import { loadSavedRecord } from './loadSavedRecord';
import { describeVerificationRunFinish } from './verificationCheckErrors';
import {
  VERIFICATION_NOT_ON_PLAN,
  VERIFICATION_RUN_BLOCKED,
} from './verificationReportSummary';
import {
  isVerificationRunActive,
  markVerificationRunFinished,
  markVerificationRunStarted,
  readVerificationRun,
  verificationRunStorageKey,
} from './verificationRunLock';

interface RefusalReport {
  /** How loudly it is reported (notistack is untyped in custom.d.ts). */
  variant: 'info' | 'warning' | 'error';
  /** Kept as the record's standing, so the views can say why it can't run. */
  standing?: boolean;
  /** Said in the product's words instead of the service's message. */
  message?: string;
}

// How each refusal from the entity service is reported: a run already going or
// nothing to read is information, running out of credits a warning, anything
// else an error.
const REFUSALS: Record<VerificationRunRefusal, RefusalReport> = {
  'in-progress': { variant: 'info' },
  'nothing-to-verify': { variant: 'info' },
  'no-credits': { variant: 'warning' },
  // Not the internal entitlement's name.
  'addon-required': {
    variant: 'error',
    standing: true,
    message: VERIFICATION_NOT_ON_PLAN,
  },
  'database-off': { variant: 'error', standing: true },
  'not-found': { variant: 'error' },
  'membership-unavailable': { variant: 'error' },
  unreachable: { variant: 'error' },
};

interface UseVerificationRunOptions {
  /** The record: needs its database id and Database Entity id. */
  supplier: any;
  /** Shared by every view of this record (page, record band, other tabs). */
  entityKey: string;
  refetch: () => unknown;
}

export interface VerificationRunState {
  running: boolean;
  runStartedAt: number | null;
  /** A refusal that describes the record's standing (no add-on, turned off). */
  refusal: VerificationRunRefusal | null;
  /**
   * The record's database is being screened. Screening verifies any check
   * without a stored result, so a run started now could bill a check twice;
   * `run` refuses until the screen finishes.
   */
  databaseScreening: boolean;
  run: () => Promise<void>;
}

/**
 * One Verification Run, shared by the verification page and the record band.
 * The entity service runs the checks and stores their results and Check
 * Errors on the record; this marks the run in localStorage so a reload or a
 * second tab in this browser sees it in progress, and reports how it ended.
 */
export const useVerificationRun = ({
  supplier,
  entityKey,
  refetch,
}: UseVerificationRunOptions): VerificationRunState => {
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  const [refusal, setRefusal] = useState<VerificationRunRefusal | null>(null);
  const { cur_user } = useContext(GlobalContext);
  // The same query (and stages) the record page reads for its own screening
  // state, so this shares its cache rather than adding a request. Entity
  // records read the entity service; anything else the legacy route.
  const { data: screeningStatus } = useGetDatabaseScreeningStatus(
    supplier?.database_id ? String(supplier.database_id) : undefined,
    !supplier?.type || supplier.type === 'supplier',
  );
  const databaseScreening = Boolean(
    screeningStatus?.inProgress ||
      screeningStatus?.aiInProgress ||
      screeningStatus?.verificationInProgress,
  );
  const mountedRef = useRef(true);
  // This view is running the checks itself.
  const ownRunRef = useRef(false);
  // This view is showing a run another tab (or this one before a reload) owns.
  const watchedRunRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Storage events cover other tabs; the poll catches a run whose tab closed
  // before it could say it finished.
  const syncStoredRun = useCallback(() => {
    if (ownRunRef.current) return;
    const stored = readVerificationRun(entityKey);
    if (stored && isVerificationRunActive(stored)) {
      watchedRunRef.current = stored.startedAt;
      setRunStartedAt(stored.startedAt);
      return;
    }
    if (watchedRunRef.current !== null) {
      watchedRunRef.current = null;
      setRunStartedAt(null);
      refetch();
    }
  }, [entityKey, refetch]);

  useEffect(() => {
    syncStoredRun();
    const onStorage = (event: StorageEvent) => {
      if (event.key === verificationRunStorageKey(entityKey)) syncStoredRun();
    };
    window.addEventListener('storage', onStorage);
    const poll = window.setInterval(() => {
      if (watchedRunRef.current !== null) syncStoredRun();
    }, 3000);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.clearInterval(poll);
    };
  }, [entityKey, syncStoredRun]);

  const run = async () => {
    if (!supplier || runStartedAt !== null) return;
    if (databaseScreening) {
      enqueueSnackbar(VERIFICATION_RUN_BLOCKED.databaseScreening, {
        variant: 'info',
      });
      return;
    }
    if (isVerificationRunActive(readVerificationRun(entityKey))) {
      syncStoredRun();
      enqueueSnackbar(
        'Verification is already running for this record in another tab.',
        { variant: 'info' },
      );
      return;
    }

    const entityName = supplier?.name || 'this entity';
    const startedAt = Date.now();
    let finishedChecks: VerificationRunCheck[] | null = null;
    ownRunRef.current = true;
    markVerificationRunStarted(entityKey, startedAt);
    setRunStartedAt(startedAt);
    try {
      const outcome = await runBusinessVerification(supplier);

      if (!outcome.ok) {
        const report = REFUSALS[outcome.refusal];
        if (mountedRef.current) {
          setRefusal(report.standing ? outcome.refusal : null);
        }
        enqueueSnackbar(report.message ?? outcome.message, {
          variant: report.variant,
        });
        return;
      }

      if (mountedRef.current) setRefusal(null);
      await refetch();
      finishedChecks = outcome.checks;
    } finally {
      // Runs to completion even if the reviewer has left the page, so the
      // lock is always released.
      markVerificationRunFinished(entityKey, startedAt);
      ownRunRef.current = false;
      if (mountedRef.current) setRunStartedAt(null);
    }

    if (!finishedChecks) return;
    // The snackbar and the report are built from the record as the service
    // just saved it rather than the copy this view loaded earlier.
    const savedRecord = await loadSavedRecord(
      supplier,
      selectedTeamId(cur_user),
    ).catch(() => null);
    const { variant, message } = describeVerificationRunFinish({
      runChecks: finishedChecks,
      checks: buildEntityVerificationReportChecks(savedRecord || supplier),
      entityName: entityName,
    });
    enqueueSnackbar(message, { variant: variant });

    // The report downloads on its own once the run has written fresh results.
    if (!finishedChecks.some((check) => check.status === 'completed')) return;
    const downloaded = savedRecord
      ? await downloadVerificationReport({
          supplier: savedRecord,
          databaseName: savedRecord.database_name,
          preparedBy: reportPreparedBy(cur_user),
          runChecks: finishedChecks,
        }).then(
          () => true,
          () => false,
        )
      : false;
    if (!downloaded) {
      enqueueSnackbar(
        'Verification finished, but the PDF report could not be created. Try again from the verification page.',
        { variant: 'warning' },
      );
    }
  };

  return {
    running: runStartedAt !== null,
    runStartedAt: runStartedAt,
    refusal: refusal,
    databaseScreening: databaseScreening,
    run: run,
  };
};
